import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { TurntableBase, TurntablePlatter, RecordImage, RecordGrooves } from '../components/dj-kit/Turntable';
import PlayPauseButton from '../components/dj-kit/PlayPauseButton';
import { initializeAudio, ensureAudioNodes as ensureAudioNodesUtil } from '../utils/audioUtils';

const Container = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
`;

const TurntableWrapper = styled.div`
    position: relative;
    width: 500px;
    height: 500px;
`;

const PlayButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

// 调试面板按钮
const DebugPanelButton = styled.button`
    position: fixed;
    top: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    font-size: 18px;
    z-index: 10000;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
    
    &:hover {
        background: rgba(0, 0, 0, 0.9);
        border-color: rgba(255, 255, 255, 0.4);
        transform: scale(1.05);
    }
    
    &:active {
        transform: scale(0.95);
    }
    
    @media (max-width: 768px) {
        top: auto;
        bottom: 10px;
        right: 10px;
    }
`;

// 调试面板
const DebugPanel = styled.div`
    position: fixed;
    top: 60px;
    right: 10px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: ${props => props.isOpen ? '12px 16px' : '0 16px'};
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    z-index: 10000;
    min-width: 200px;
    line-height: 1.6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-height: ${props => props.isOpen ? '500px' : '0'};
    overflow: hidden;
    opacity: ${props => props.isOpen ? '1' : '0'};
    transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease;
    transform-origin: top;
    
    @media (max-width: 768px) {
        top: calc(100vh - 550px);
        bottom: auto;
        right: 10px;
        left: 10px;
        min-width: auto;
        max-width: calc(100vw - 20px);
        max-height: ${props => props.isOpen ? 'calc(100vh - 60px)' : '0'};
    }
`;

const BASE_BPM = 95;
const BASE_WIDTH = 500;
const BASE_HEIGHT = 500;
const PLATTER_SIZE = 300;

// 计算一圈对应的时间
const getTurntableAnimationDuration = () => (60 / BASE_BPM) * 20;

const MusicTestPage = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1); // 用于显示当前播放速度

    // Audio refs
    const audioContextRef = useRef(null);
    const audioBufferRef = useRef(null);
    const audioBufferReversedRef = useRef(null); // 反向音频 buffer
    const sourceNodeRef = useRef(null);
    const startTimeRef = useRef(0);
    const highFilterRef = useRef(null);
    const midFilterRef = useRef(null);
    const lowFilterRef = useRef(null);
    const gainNodeRef = useRef(null);
    const animationFrameRef = useRef(null);
    const isProcessingPlayPauseRef = useRef(false);
    
    // Drag refs
    const rotationRef = useRef(0);
    const lastMouseAngleRef = useRef(0);
    const cumulativeAngleRef = useRef(0);
    const wasPlayingBeforeDragRef = useRef(false);
    const platterRef = useRef(null);
    const setPlaybackTimeRef = useRef(null);
    
    // Scratch speed calculation refs
    const previousRotationRef = useRef(0); // 上一帧的角度（用于计算速度）
    const timestampPreviousRef = useRef(performance.now()); // 上一帧的时间戳
    const draggingSpeedsRef = useRef([]); // 速度历史（用于平滑）
    const currentPlaybackSpeedRef = useRef(1); // 当前播放速度
    const isReversedRef = useRef(false); // 是否反向播放

    const audioPath = '/audio/its-ok33y.mp3';

    // 创建反向音频 buffer（用于反向播放）
    const createReversedAudioBuffer = useCallback((audioContext, audioBuffer) => {
        const numberOfChannels = audioBuffer.numberOfChannels;
        const length = audioBuffer.length;
        const sampleRate = audioBuffer.sampleRate;
        const reversedBuffer = audioContext.createBuffer(numberOfChannels, length, sampleRate);
        
        for (let channel = 0; channel < numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const reversedData = reversedBuffer.getChannelData(channel);
            // 反转数据
            for (let i = 0; i < length; i++) {
                reversedData[i] = originalData[length - 1 - i];
            }
        }
        
        return reversedBuffer;
    }, []);

    // 初始化音频
    useEffect(() => {
        if (!audioPath) return;

        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            return;
        }

        const initAudio = async () => {
            try {
                const fullPath = `${process.env.PUBLIC_URL || ''}${audioPath}`;
                const { audioContext, audioBuffer, highFilter, midFilter, lowFilter, gainNode } = 
                    await initializeAudio(fullPath, {
                        volume: 100,
                        eqValues: { high: 50, mid: 50, low: 50 }
                    });

                audioContextRef.current = audioContext;
                audioBufferRef.current = audioBuffer;
                highFilterRef.current = highFilter;
                midFilterRef.current = midFilter;
                lowFilterRef.current = lowFilter;
                gainNodeRef.current = gainNode;
                
                // 创建反向音频 buffer（用于反向播放）
                try {
                    const reversedBuffer = createReversedAudioBuffer(audioContext, audioBuffer);
                    audioBufferReversedRef.current = reversedBuffer;
                } catch (err) {
                    console.warn('Failed to create reversed audio buffer:', err);
                }
                
                setDuration(audioBuffer.duration);
            } catch (err) {
                console.error('Failed to initialize audio:', err);
            }
        };

        initAudio();

        return () => {
            stopAnimationLoop();
            if (sourceNodeRef.current) {
                try {
                    sourceNodeRef.current.stop();
                } catch (e) {
                    // 忽略已经停止的错误
                }
                sourceNodeRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createReversedAudioBuffer, stopAnimationLoop stable
    }, [audioPath]);

    // 确保音频节点
    const ensureAudioNodes = useCallback(() => {
        const audioContext = audioContextRef.current;
        if (!audioContext) {
            return false;
        }

        const nodes = ensureAudioNodesUtil(
            audioContext,
            {
                highFilter: highFilterRef.current,
                midFilter: midFilterRef.current,
                lowFilter: lowFilterRef.current,
                gainNode: gainNodeRef.current
            },
            {
                volume: 100,
                eqValues: { high: 50, mid: 50, low: 50 }
            }
        );

        if (!nodes) {
            return false;
        }

        highFilterRef.current = nodes.highFilter;
        midFilterRef.current = nodes.midFilter;
        lowFilterRef.current = nodes.lowFilter;
        gainNodeRef.current = nodes.gainNode;

        return true;
    }, []);

    // 计算一圈对应的时间
    const turntableAnimationDuration = getTurntableAnimationDuration();

    // 时间到旋转角度转换
    const timeToRotation = (time) => {
        if (!duration) return 0;
        const rotations = time / turntableAnimationDuration;
        return rotations * 360;
    };

    // 旋转角度到时间转换
    const rotationToTime = useCallback((angle) => {
        if (!duration) return 0;
        const rotations = angle / 360;
        let time = rotations * turntableAnimationDuration;
        while (time < 0) time += duration; // 处理负数
        return time;
    }, [duration, turntableAnimationDuration]);

    // 计算基准角度变化率（每毫秒的角度变化，度）
    const calculateBaseAnglePerMs = useCallback(() => {
        return 360 / (turntableAnimationDuration * 1000);
    }, [turntableAnimationDuration]);

    // 速度平滑处理（移动平均）
    const smoothSpeed = useCallback((speed) => {
        const speeds = draggingSpeedsRef.current;
        speeds.push(speed);
        if (speeds.length > 10) speeds.shift();
        const avg = speeds.reduce((sum, s) => sum + s, 0) / speeds.length;
        return Math.max(-4, Math.min(4, avg));
    }, []);

    // 停止当前 source
    const stopSource = useCallback(() => {
        if (sourceNodeRef.current) {
            sourceNodeRef.current.onended = null;
            try {
                sourceNodeRef.current.stop();
            } catch (e) {
                // 忽略错误
            }
            sourceNodeRef.current = null;
        }
    }, []);

    // 停止动画循环
    const stopAnimationLoop = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    // 通用的 onended 回调
    const createOnEndedCallback = useCallback(() => {
        return () => {
            if (!sourceNodeRef.current) return;
            setIsPlaying(false);
            setCurrentTime(0);
            setRotation(0);
            rotationRef.current = 0;
            stopAnimationLoop();
            sourceNodeRef.current = null;
        };
    }, [stopAnimationLoop]);

    // 创建并启动 audio source
    const createAndStartSource = useCallback((offset, playbackRateValue, useReversed = false) => {
        if (!audioContextRef.current || !audioBufferRef.current) return false;
        
        const audioContext = audioContextRef.current;
        const buffer = useReversed && audioBufferReversedRef.current 
            ? audioBufferReversedRef.current 
            : audioBufferRef.current;
        const highFilter = highFilterRef.current;
        
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.playbackRate.value = playbackRateValue;
        source.connect(highFilter);
        source.onended = createOnEndedCallback();
        
        sourceNodeRef.current = source;
        startTimeRef.current = audioContext.currentTime - offset / playbackRateValue;
        source.start(0, offset);
        
        return true;
    }, [createOnEndedCallback]);

    // 更新音频播放速度
    const updatePlaybackSpeed = useCallback((speed, isReversed, currentTime) => {
        if (!sourceNodeRef.current || !audioContextRef.current) return;
        
        const audioContext = audioContextRef.current;
        const speedAbs = Math.max(0.001, Math.abs(speed));
        
        // 如果方向改变，需要切换 buffer 并重新创建 source
        if (isReversed !== isReversedRef.current) {
            stopSource();
            
            if (!ensureAudioNodes()) {
                console.error('Failed to initialize audio nodes');
                return;
            }
            
            const offset = isReversed ? duration - currentTime : currentTime;
            createAndStartSource(offset, speedAbs, isReversed);
            isReversedRef.current = isReversed;
        } else {
            // 只更新播放速度
            const now = audioContext.currentTime;
            sourceNodeRef.current.playbackRate.cancelScheduledValues(now);
            sourceNodeRef.current.playbackRate.linearRampToValueAtTime(speedAbs, now);
        }
        
        currentPlaybackSpeedRef.current = speed;
    }, [duration, ensureAudioNodes, stopSource, createAndStartSource]);

    // 获取事件坐标（支持鼠标和触摸）
    const getEventCoordinates = (e) => {
        if (e.pointerType) {
            return { clientX: e.clientX, clientY: e.clientY };
        }
        if (e.touches && e.touches.length > 0) {
            return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
        }
        return { clientX: e.clientX, clientY: e.clientY };
    };

    // 计算鼠标相对于唱盘中心的角度
    const getAngle = (centerX, centerY, x, y) => {
        const dx = x - centerX;
        const dy = y - centerY;
        return Math.atan2(dy, dx) * (180 / Math.PI);
    };

    // 计算角度差，处理跨越 0/360 度边界的情况
    const getAngleDelta = (currentAngle, lastAngle, currentCumulativeAngle) => {
        let delta = currentAngle - lastAngle;
        // 如果角度差超过 180 度，说明跨越了边界，需要调整
        if (delta > 180) {
            delta -= 360;
        } else if (delta < -180) {
            delta += 360;
        }
        // 更新累积角度
        const newCumulativeAngle = currentCumulativeAngle + delta;
        return { delta, cumulativeAngle: newCumulativeAngle };
    };

    // 启动进度更新循环（支持 scratch 效果）
    const startProgressUpdate = useCallback(() => {
        stopAnimationLoop(); // 先停止现有循环

        // 初始化时间戳和角度
        timestampPreviousRef.current = performance.now();
        previousRotationRef.current = rotationRef.current || rotation;

        let lastUpdateTime = 0;
        const baseAnglePerMs = calculateBaseAnglePerMs();
        
        const updateProgress = () => {
            const now = performance.now();
            const timeDelta = now - timestampPreviousRef.current;
            const currentRotation = rotationRef.current || rotation;
            const angleDelta = currentRotation - previousRotationRef.current;
            
            // 计算播放速度
            const baseAngle = baseAnglePerMs * timeDelta;
            const playbackSpeed = timeDelta > 0 && baseAngle !== 0 ? (angleDelta / baseAngle || 0) : 1;
            const isReversed = angleDelta < 0;
            
            // 如果正在拖曳，平滑速度并实时更新播放速度
            if (isDraggingRef.current && wasPlayingBeforeDragRef.current) {
                const smoothedSpeed = smoothSpeed(playbackSpeed);
                const currentTime = rotationToTime(currentRotation) % duration;
                
                if (audioContextRef.current && sourceNodeRef.current) {
                    updatePlaybackSpeed(smoothedSpeed, isReversed, currentTime);
                }
                setCurrentTime(currentTime);
                setPlaybackSpeed(smoothedSpeed);
            } else if (!isDraggingRef.current && audioContextRef.current && sourceNodeRef.current) {
                // 不拖曳时，正常更新进度
                const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
                const newTime = Math.min(Math.max(0, elapsed), duration);
                const newRotation = timeToRotation(newTime);
                
                setRotation(newRotation);
                rotationRef.current = newRotation;
                
                if (Date.now() - lastUpdateTime >= 50) {
                    setCurrentTime(newTime);
                    lastUpdateTime = Date.now();
                }
                
                if (newTime >= duration) {
                    stopAnimationLoop();
                }
            } else if (!audioContextRef.current || !sourceNodeRef.current) {
                stopAnimationLoop();
            }
            
            // 更新上一帧的值
            previousRotationRef.current = currentRotation;
            timestampPreviousRef.current = now;
            
            // 继续循环
            animationFrameRef.current = requestAnimationFrame(updateProgress);
        };
        
        animationFrameRef.current = requestAnimationFrame(updateProgress);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- rotationToTime intentional deps
    }, [duration, timeToRotation, rotation, calculateBaseAnglePerMs, smoothSpeed, updatePlaybackSpeed, stopAnimationLoop]);

    // 强制设置播放时间（可以强制跳转到指定位置）
    const setPlaybackTime = useCallback(async (newTime, shouldPlay = false) => {
        console.log('[MusicTestPage] setPlaybackTime', newTime, shouldPlay);
        if (!audioContextRef.current || !audioBufferRef.current || !duration) return;

        const safeTime = Math.max(0, Math.min(duration, newTime));
        const newRotation = timeToRotation(safeTime);
        setCurrentTime(safeTime);
        setRotation(newRotation);
        rotationRef.current = newRotation;

        if ((isPlaying && sourceNodeRef.current) || shouldPlay) {
            stopSource();
            stopAnimationLoop();

            if (!ensureAudioNodes()) {
                console.error('Failed to initialize audio nodes');
                return;
            }

            if (audioContextRef.current.state === 'suspended') {
                try {
                    await audioContextRef.current.resume();
                } catch (err) {
                    console.error('Failed to resume audio context:', err);
                    return;
                }
            }

            if (createAndStartSource(safeTime, 1.0, false)) {
                setIsPlaying(true);
                startProgressUpdate();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stopAnimationLoop stable
    }, [isPlaying, duration, timeToRotation, ensureAudioNodes, stopSource, createAndStartSource, startProgressUpdate]);

    // 保存 setPlaybackTime 到 ref，以便在其他地方使用
    useEffect(() => {
        setPlaybackTimeRef.current = setPlaybackTime;
    }, [setPlaybackTime]);

    // 根据旋转角度设置播放位置
    const setPlaybackTimeFromRotation = useCallback(async (rotation, shouldPlay = false) => {
        if (setPlaybackTimeRef.current) {
            const finalTime = rotationToTime(rotation);
            const safeStartTime = Math.max(0, Math.min(duration, finalTime % duration));
            await setPlaybackTimeRef.current(safeStartTime, shouldPlay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- rotationToTime in ref flow
    }, [duration, rotationToTime]);

    // 处理唱盘按下（开始拖曳）
    const handleTurntablePointerDown = (e) => {
        e.preventDefault();
        // 设置 pointer capture
        if (e.currentTarget.setPointerCapture) {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        
        const coords = getEventCoordinates(e);
        const rect = e.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const angle = getAngle(centerX, centerY, coords.clientX, coords.clientY);
        
        const currentRotation = rotationRef.current || rotation;
        
        setIsDragging(true);
        isDraggingRef.current = true;
        rotationRef.current = currentRotation;
        lastMouseAngleRef.current = angle;
        cumulativeAngleRef.current = currentRotation;
        previousRotationRef.current = currentRotation;
        timestampPreviousRef.current = performance.now();
        draggingSpeedsRef.current = [];
        currentPlaybackSpeedRef.current = 1;
        wasPlayingBeforeDragRef.current = isPlaying && sourceNodeRef.current !== null;
    };

    // 处理唱盘拖曳移动
    const handleTurntableMouseMove = (e) => {
        if (!isDragging) return;
        
        const coords = getEventCoordinates(e);
        const rect = e.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const currentAngle = getAngle(centerX, centerY, coords.clientX, coords.clientY);
        
        // 计算角度增量，处理跨越边界的情况
        const { cumulativeAngle } = getAngleDelta(currentAngle, lastMouseAngleRef.current, cumulativeAngleRef.current);
        const newRotation = cumulativeAngle;
        
        setRotation(newRotation);
        rotationRef.current = newRotation;
        lastMouseAngleRef.current = currentAngle;
        cumulativeAngleRef.current = cumulativeAngle;
        
        // 更新显示时间（速度会在循环中实时计算和更新）
        const newTime = rotationToTime(newRotation);
        setCurrentTime(Math.max(0, Math.min(duration, newTime % duration)));
    };

    // 处理唱盘释放（结束拖曳）
    const handleTurntableMouseUp = useCallback(async () => {
        if (!isDragging) return;
        
        setIsDragging(false);
        isDraggingRef.current = false;
        draggingSpeedsRef.current = [];
        currentPlaybackSpeedRef.current = 1;
        setPlaybackSpeed(1);
        
        const finalRotation = rotationRef.current;
        
        // 重置播放速度到正常速度
        if (wasPlayingBeforeDragRef.current && sourceNodeRef.current && audioContextRef.current) {
            const now = audioContextRef.current.currentTime;
            sourceNodeRef.current.playbackRate.cancelScheduledValues(now);
            sourceNodeRef.current.playbackRate.linearRampToValueAtTime(1.0, now);
        }
        
        await setPlaybackTimeFromRotation(finalRotation, wasPlayingBeforeDragRef.current);
        wasPlayingBeforeDragRef.current = false;
        isReversedRef.current = false;
    }, [isDragging, setPlaybackTimeFromRotation]);

    // 同步 rotationRef 和 rotation state
    useEffect(() => {
        rotationRef.current = rotation;
    }, [rotation]);

    // 处理全局拖曳事件
    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (e) => {
            if (platterRef.current) {
                handleTurntableMouseMove(e);
            }
        };

        const handlePointerUp = async (e) => {
            if (e.target && e.target.releasePointerCapture) {
                e.target.releasePointerCapture(e.pointerId);
            }
            await handleTurntableMouseUp();
        };

        document.body.addEventListener('pointermove', handlePointerMove);
        document.body.addEventListener('pointerup', handlePointerUp);

        return () => {
            document.body.removeEventListener('pointermove', handlePointerMove);
            document.body.removeEventListener('pointerup', handlePointerUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- handleTurntableMouseMove from ref
    }, [isDragging, duration, handleTurntableMouseUp, rotationToTime]);

    // 播放/暂停
    const handlePlayPause = async () => {
        if (isProcessingPlayPauseRef.current) {
            return;
        }
        
        isProcessingPlayPauseRef.current = true;
        
        try {
            if (!audioContextRef.current || !audioBufferRef.current) {
                console.error('Audio not initialized');
                isProcessingPlayPauseRef.current = false;
                return;
            }

            if (audioContextRef.current.state === 'suspended') {
                try {
                    await audioContextRef.current.resume();
                } catch (err) {
                    console.error('Failed to resume audio context:', err);
                    isProcessingPlayPauseRef.current = false;
                    return;
                }
            }

            const nodesReady = ensureAudioNodes();
            if (!nodesReady) {
                console.error('Failed to initialize audio nodes');
                isProcessingPlayPauseRef.current = false;
                return;
            }

            if (isPlaying) {
                // 暂停 - 保存当前播放时间
                if (audioContextRef.current && startTimeRef.current) {
                    const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
                    setCurrentTime(Math.min(Math.max(0, elapsed), duration));
                }
                stopSource();
                stopAnimationLoop();
                setIsPlaying(false);
            } else {
                // 播放
                if (createAndStartSource(currentTime, 1.0, false)) {
                    setIsPlaying(true);
                    startProgressUpdate();
                }
            }
        } catch (err) {
            console.error('Error in play/pause:', err);
        } finally {
            isProcessingPlayPauseRef.current = false;
        }
    };

    // 暴露 setPlaybackTime 到 window 对象，方便在控制台测试
    useEffect(() => {
        window.setPlaybackTime = setPlaybackTime;
        return () => {
            delete window.setPlaybackTime;
        };
    }, [setPlaybackTime]);

    // 计算调试信息
    const actualRotations = rotation / 360;

    return (
        <Container>
            <DebugPanelButton onClick={() => setIsDebugPanelOpen(!isDebugPanelOpen)}>
                🔧
            </DebugPanelButton>
            <DebugPanel isOpen={isDebugPanelOpen}>
                <div><strong>播放秒数:</strong> {currentTime.toFixed(2)}s</div>
                <div><strong>总时长:</strong> {duration.toFixed(2)}s</div>
                <div><strong>显示角度:</strong> {rotation.toFixed(1)}°</div>
                <div><strong>实际圈数:</strong> {actualRotations.toFixed(3)}</div>
                <div><strong>角度对应时间:</strong> {rotationToTime(rotation).toFixed(2)}s</div>
                <div><strong>BPM:</strong> {BASE_BPM}</div>
                <div><strong>一圈时长:</strong> {turntableAnimationDuration.toFixed(2)}s</div>
                <div><strong>播放状态:</strong> {isPlaying ? '播放中' : '暂停'}</div>
                <div><strong>拖拽状态:</strong> {isDragging ? '拖拽中' : '未拖拽'}</div>
                {isDragging && (
                    <>
                        <div><strong>播放速度:</strong> {playbackSpeed.toFixed(2)}x</div>
                        <div><strong>播放方向:</strong> {isReversedRef.current ? '反向' : '正向'}</div>
                    </>
                )}
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                    <div style={{ fontSize: '10px', opacity: 0.8 }}>
                        <strong>控制台测试:</strong><br/>
                        设置播放时间: <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 4px', borderRadius: '2px' }}>setPlaybackTime(30)</code><br/>
                        设置并播放: <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 4px', borderRadius: '2px' }}>setPlaybackTime(30, true)</code>
                    </div>
                </div>
            </DebugPanel>
            <TurntableWrapper>
                <TurntableBase 
                    baseWidth={BASE_WIDTH} 
                    baseHeight={BASE_HEIGHT}
                >
                    <TurntablePlatter
                        ref={platterRef}
                        size={PLATTER_SIZE}
                        rotation={rotation}
                        layout={{}}
                        onPointerDown={handleTurntablePointerDown}
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    >
                        <RecordImage />
                        <RecordGrooves />
                    </TurntablePlatter>
                </TurntableBase>
            </TurntableWrapper>

            <PlayButtonWrapper>
                <PlayPauseButton
                    isPlaying={isPlaying}
                    onClick={handlePlayPause}
                    layout={{
                        width: 80,
                        height: 80,
                        bottom: 0,
                        left: 0
                    }}
                >
                    {isPlaying ? '⏸' : '▶'}
                </PlayPauseButton>
            </PlayButtonWrapper>
        </Container>
    );
};

export default MusicTestPage;


